import { __extends } from "tslib";
import { emitEvent, listenOwnProperty } from '@tyler-components-web/core';
import { getActiveElement } from 'shadow-utils';
import { CalendarDropdown } from '../calendar-dropdown';
import { randomChars } from '../core';
import { BaseAdapter } from '../core/adapters/base-adapter';
import { DateInputMask } from '../core/mask/date-input-mask';
import { ICON_BUTTON_CONSTANTS } from '../icon-button';
import { TEXT_FIELD_CONSTANTS } from '../text-field';
import { DATEPICKER_CONSTANTS } from './datepicker-constants';
import { ICON_CONSTANTS } from '../icon';
var DatepickerAdapter = /** @class */ (function (_super) {
    __extends(DatepickerAdapter, _super);
    function DatepickerAdapter(component) {
        var _this = _super.call(this, component) || this;
        _this._identifier = randomChars();
        return _this;
    }
    DatepickerAdapter.prototype.initialize = function () {
        this._inputElement = this._component.querySelector(DATEPICKER_CONSTANTS.selectors.INPUT);
        this._calendarDropdown = new CalendarDropdown(this._inputElement);
    };
    DatepickerAdapter.prototype.destroy = function () {
        this._calendarDropdown.destroy();
    };
    DatepickerAdapter.prototype.initializeMask = function (options) {
        if (this._inputMask) {
            this._inputMask.destroy();
        }
        this._inputMask = new DateInputMask(this._inputElement, options);
    };
    DatepickerAdapter.prototype.destroyMask = function () {
        if (this._inputMask) {
            this._inputMask.destroy();
            this._inputMask = undefined;
        }
    };
    DatepickerAdapter.prototype.initializeAccessibility = function () {
        this._inputElement.setAttribute('autocomplete', 'off');
        this._inputElement.setAttribute('autocorrect', 'off');
        this._inputElement.setAttribute('autocapitalize', 'off');
        this._inputElement.setAttribute('spellcheck', 'false');
        this._inputElement.setAttribute('role', 'combobox');
        this._inputElement.setAttribute('aria-live', 'assertive');
        this._inputElement.setAttribute('aria-atomic', 'true');
        this._inputElement.setAttribute('aria-haspopup', 'true');
        this._inputElement.setAttribute('aria-expanded', 'false');
        this._inputElement.setAttribute('aria-owns', "tcw-datepicker-" + this._identifier);
    };
    DatepickerAdapter.prototype.addInputListener = function (type, listener) {
        this._inputElement.addEventListener(type, listener);
    };
    DatepickerAdapter.prototype.removeInputListener = function (type, listener) {
        if (this._inputElement) {
            this._inputElement.removeEventListener(type, listener);
        }
    };
    DatepickerAdapter.prototype.setInputValueChangedListener = function (context, listener) {
        if (this._inputElement.hasOwnProperty('value')) {
            return;
        }
        listenOwnProperty(context, this._inputElement, 'value', listener);
    };
    DatepickerAdapter.prototype.addToggleListener = function (type, listener) {
        if (this._toggleElement) {
            this._toggleElement.addEventListener(type, listener);
        }
    };
    DatepickerAdapter.prototype.removeToggleListener = function (type, listener) {
        if (this._toggleElement) {
            this._toggleElement.removeEventListener(type, listener);
        }
    };
    DatepickerAdapter.prototype.hasInputElement = function () {
        return !!this._inputElement;
    };
    DatepickerAdapter.prototype.attachCalendar = function (calendarConfig) {
        Object.assign(this._calendarDropdown, calendarConfig);
        this._calendarDropdown.id = "tcw-datepicker-" + this._identifier;
        this._calendarDropdown.open = true;
        this._inputElement.setAttribute('aria-expanded', 'true');
    };
    DatepickerAdapter.prototype.detachCalendar = function () {
        if (this._inputElement) {
            this._inputElement.setAttribute('aria-expanded', 'false');
            this._inputElement.removeAttribute('aria-activedescendant');
        }
        if (this._calendarDropdown && this._calendarDropdown.open) {
            this._calendarDropdown.open = false;
        }
    };
    DatepickerAdapter.prototype.setActiveDescendant = function (id) {
        this._inputElement.setAttribute('aria-activedescendant', id);
    };
    DatepickerAdapter.prototype.setCalendarValue = function (value) {
        if (this._calendarDropdown) {
            this._calendarDropdown.value = value;
        }
    };
    DatepickerAdapter.prototype.setCalendarMinDate = function (value) {
        if (this._calendarDropdown) {
            this._calendarDropdown.min = value;
        }
    };
    DatepickerAdapter.prototype.setCalendarMaxDate = function (value) {
        if (this._calendarDropdown) {
            this._calendarDropdown.max = value;
        }
    };
    DatepickerAdapter.prototype.setCalendarBlockedDates = function (value) {
        if (this._calendarDropdown) {
            this._calendarDropdown.blockedDates = value;
        }
    };
    DatepickerAdapter.prototype.setCalendarDisabledDaysOfWeek = function (value) {
        if (this._calendarDropdown) {
            this._calendarDropdown.disabledDaysOfWeek = value;
        }
    };
    DatepickerAdapter.prototype.propagateCalendarKey = function (key, isShiftKey) {
        if (this._calendarDropdown) {
            this._calendarDropdown.propagateKey(key, isShiftKey);
        }
    };
    DatepickerAdapter.prototype.setInputValue = function (value, emitEvents) {
        if (this._inputElement.value === value) {
            return;
        }
        this._inputElement.value = value;
        if (this._inputMask) {
            this._inputMask.updateMask();
        }
        if (emitEvents) {
            this._emitInputEvent('change');
            this._emitInputEvent('input');
        }
    };
    DatepickerAdapter.prototype.setDisableDayCallback = function (disableDayCallback) {
        if (this._calendarDropdown) {
            this._calendarDropdown.disableDayCallback = disableDayCallback;
        }
    };
    DatepickerAdapter.prototype.isInputDisabled = function () {
        return this._inputElement.disabled;
    };
    DatepickerAdapter.prototype.isInputFocused = function () {
        return getActiveElement() === this._inputElement;
    };
    DatepickerAdapter.prototype.getInputValue = function () {
        return this._inputMask ? this._inputMask.maskedValue : this._inputElement.value;
    };
    DatepickerAdapter.prototype.setDisabled = function (isDisabled) {
        this._inputElement.disabled = isDisabled;
        this._inputElement.setAttribute('aria-disabled', isDisabled.toString());
        if (this._toggleElement) {
            this._toggleElement.setAttribute('aria-disabled', isDisabled.toString());
            if (this._toggleElement.hasOwnProperty('disabled')) {
                this._toggleElement['disabled'] = isDisabled;
            }
        }
    };
    DatepickerAdapter.prototype.emitInputEvent = function (type, data) {
        emitEvent(this._inputElement, type, data);
    };
    DatepickerAdapter.prototype.tryCreateToggle = function () {
        var tcwTextField = this._component.querySelector(TEXT_FIELD_CONSTANTS.elementName);
        var toggleElement = this._component.querySelector(DATEPICKER_CONSTANTS.selectors.TOGGLE);
        if (tcwTextField) {
            var existingIconButton = tcwTextField.querySelector(ICON_BUTTON_CONSTANTS.elementName + "[slot=trailing]");
            if (existingIconButton || toggleElement) {
                this._toggleElement = (existingIconButton || toggleElement);
                return;
            }
            var iconButtonElement = document.createElement(ICON_BUTTON_CONSTANTS.elementName);
            iconButtonElement.slot = 'trailing';
            iconButtonElement.dense = true;
            iconButtonElement.densityLevel = 3;
            iconButtonElement.style.marginRight = '4px'; // Override default trailing slot margin in text-field
            var buttonElement = document.createElement('button');
            buttonElement.type = 'button';
            buttonElement.tabIndex = -1;
            buttonElement.setAttribute('aria-label', 'Toggle calendar');
            var iconElement = document.createElement(ICON_CONSTANTS.elementName);
            iconElement.name = 'insert_invitation';
            buttonElement.appendChild(iconElement);
            iconButtonElement.appendChild(buttonElement);
            tcwTextField.appendChild(iconButtonElement);
            this._toggleElement = iconButtonElement;
        }
        else if (toggleElement) {
            this._toggleElement = toggleElement;
        }
    };
    DatepickerAdapter.prototype.tryFocusInput = function () {
        this._inputElement.select();
    };
    DatepickerAdapter.prototype.tryBlurInput = function () {
        this._inputElement.blur();
    };
    DatepickerAdapter.prototype.selectInputText = function () {
        this._inputElement.select();
    };
    DatepickerAdapter.prototype._emitInputEvent = function (type) {
        this._inputElement.dispatchEvent(new Event(type));
    };
    return DatepickerAdapter;
}(BaseAdapter));
export { DatepickerAdapter };
